import { Outlet, useParams } from 'react-router';
import { useInspection } from '../../../api/inspections';
import { isoToParts } from '../../../components/idsk';
import { subjectLabel } from '../../../form-engine/react/references';
import { RequireAdmin } from '../../common/RequireAdmin';
import { ForbiddenPage } from '../../common/ForbiddenPage';
import { LoadingPage } from '../../common/LoadingPage';
import { QueryError } from '../../common/QueryError';
import { WizardProvider } from './WizardContext';
import type { WizardState } from './validation';

/** /inspekcie/nova/* – creating inspections is ADMIN-only. */
export function WizardRoot() {
  return (
    <RequireAdmin>
      <WizardProvider>
        <Outlet />
      </WizardProvider>
    </RequireAdmin>
  );
}

/** /inspekcie/{id}/upravit/* – the same question pages, prefilled, for editing the basic data (ADMIN, not FINISHED). */
export function InspectionEditRoot() {
  return (
    <RequireAdmin>
      <InspectionEdit />
    </RequireAdmin>
  );
}

function InspectionEdit() {
  const { id } = useParams();
  const query = useInspection(id);
  if (query.error) return <QueryError error={query.error} asPage />;
  if (query.isPending) return <LoadingPage />;
  const i = query.data;
  if (!i.permissions.canEdit) return <ForbiddenPage />;
  const leader = i.members.find((m) => m.leader);
  const initial: WizardState = {
    name: i.name,
    // older inspections without a subject get one when their basic data are edited
    subject: i.subject ? { id: i.subject.id, label: subjectLabel(i.subject) } : null,
    type: i.type,
    task: i.task ?? '',
    taskRequired: Boolean(i.task),
    members: i.members.map((m) => ({ id: m.inspectorId, displayName: m.displayName, email: m.email })),
    leaderId: leader?.inspectorId ?? null,
    dateFrom: isoToParts(i.dateFrom),
    dateTo: isoToParts(i.dateTo),
  };
  return (
    <WizardProvider
      storageKey={`ssi.inspectionEdit.${i.id}.${i.version}`}
      initial={initial}
      basePath={`/inspekcie/${i.id}/upravit`}
      editing={{ id: i.id, version: i.version, number: i.number }}
    >
      <Outlet />
    </WizardProvider>
  );
}
